import { Dropdown, Spacer } from '@nextui-org/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import client from '../../apollo-client';
import Accordion from '../../components/Accordion';
import { BookSectionCard, ChapterCard } from '../../components/Card';
import Divider from '../../components/Divider';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import Layout from '../../components/Layout';
import { gql } from '../../generated/gql';
import { Chapter, GetBookQuery } from '../../generated/graphql';

export const GET_BOOK_IDS_QUERY = gql(`
  query getBookIds {
    allBookIds
  }
`);

const GET_BOOK_QUERY = gql(`
  query getBook($id: String!) {
    book(id: $id) {
      id
      name
      englishName
      volumes
      volume
      blurb
      author {
        name
        link
        dateOfDeath
      }
      chapters {
        id
        number
        bookSection
        name
      }
      translator
      authorName
      bookSections {
        id
        name
        sectionNumber
        chapters {
          id
          name
          number
          bookSection
        }
      }
    }
  }
`);

export async function getStaticPaths() {
  const { data } = await client.query({
    query: GET_BOOK_IDS_QUERY,
  });

  return {
    paths: data?.allBookIds?.map((id) => ({ params: { bookId: id } })),
    fallback: false, // can also be true or 'blocking'
  };
}

export async function getStaticProps({ params }: any) {
  const { data } = await client.query({
    query: GET_BOOK_QUERY,
    variables: {
      id: params?.bookId,
    },
  });

  return {
    props: {
      book: data.book,
    },
  };
}

function splitArray(array: any[], parts: number) {
  const chunkSize = Math.ceil(array.length / parts);
  const splitArrays = [];

  for (let i = 0; i < array.length; i += chunkSize) {
    splitArrays.push(array.slice(i, i + chunkSize));
  }

  return splitArrays;
}

interface BookSectionOrChapterCard {
  id: string;
  type: 'bookSection' | 'chapter';
  number: number;
  name: string;
  chapters?: Chapter[];
}

export default function Book({ book }: { book: GetBookQuery['book'] } = { book: null }) {
  const [selected, setSelected] = useState<any>(0);
  const [expandedSections, setExpandedSections] = useState<boolean[]>(book?.bookSections?.map(() => true) || []);
  const cardsToRender: BookSectionOrChapterCard[] = [];
  const router = useRouter();

  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const clampRef = useRef(null);
  const [isLineClampEnabled, setIsLineClampEnabled] = useState(true);

  useEffect(() => {
    const divElement = clampRef.current;

    if (typeof window === 'undefined' || !divElement) return;

    const computedStyles = window.getComputedStyle(divElement as any);
    const maxHeight = computedStyles.getPropertyValue('max-height');

    if (divElement) {
      const elm = divElement as any;
      setIsLineClampEnabled(elm.scrollHeight > elm.clientHeight);
    }
  }, []);

  for (const bookSection of book?.bookSections || []) {
    if (!bookSection) continue;

    cardsToRender.push({
      id: String(book?.id) || '',
      type: 'bookSection',
      number: bookSection.sectionNumber || 0,
      name: bookSection.name || '',
      // @ts-ignore
      chapters: [...bookSection.chapters].sort((a, b) => a?.number - b?.number) || [],
    });

    // // @ts-ignore
    // for (const chapter of [...(bookSection?.chapters || [])].sort((a, b) => a?.number - b?.number)) {
    //   // @ts-ignore
    //   cardsToRender.push({ ...chapter, type: 'chapter' });
    // }
  }

  return (
    <Layout className="flex flex-col min-h-screen">
      <Header
        contentFirst
        description={`Hadiths from ${book?.name} (${book?.englishName}) by ${book?.author?.name}`}
        title={book?.name + ' - ' + book?.englishName}
      />
      <div className="flex flex-col items-center justify-center md:p-4 p-1 flex-shrink">
        <div className="w-22/23 text-center md:my-8 my-6">
          <div className="md:text-6xl text-5xl font-bold cursor-pointer w-full mb-2">
            <h1>{book?.name?.split('- Volume')[0]}</h1>
            <h3 className="text-2xl font-semibold my-auto block">{book?.englishName}</h3>
            {book?.volumes && book?.volumes.length > 0 && (
              <div className="w-full">
                <Dropdown>
                  <Dropdown.Button
                    className="text-3xl m-auto w-32 font-bold bg-stone-200 dark:bg-slate-600 text-black dark:text-white p-4"
                    color="success"
                    size="lg"
                  >
                    <span className="">Volume {book.volume}</span>
                  </Dropdown.Button>
                  <Dropdown.Menu
                    aria-label="Volume picker"
                    items={book?.volumes.map((vol, i) => ({ key: vol, name: `Volume ${vol}`, index: i }))}
                    selectedKeys={[selected]}
                    selectionMode="single"
                    onAction={(e) => {
                      router.push(`/book/${e}`);
                    }}
                  >
                    {(item) => (
                      <Dropdown.Section key={(item as any)?.key}>
                        <Dropdown.Item
                          key={(item as any).key}
                          className="text-xl font-semibold text-center"
                          color={(item as any).key === 'delete' ? 'error' : 'default'}
                        >
                          {(item as any).index + 1}
                        </Dropdown.Item>
                      </Dropdown.Section>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <Spacer y={1} />
              </div>
            )}
          </div>
          <h4>
            By:{' '}
            <Link className="font-semibold text-stone-700 dark:text-stone-100" href={book?.author?.link || ''}>
              {book?.author?.name}
            </Link>
          </h4>
          <h4>
            Translator: <span className="font-semibold">{book?.translator}</span>
          </h4>
          {book?.blurb && (
            <>
              <h5
                ref={clampRef}
                className={`md:w-10/12 w-22/23 m-auto text-justify ${expanded ? 'h-full' : 'h-36 overflow-hidden'}`}
              >
                <Divider />
                {book?.blurb}
              </h5>
              {isLineClampEnabled && (
                <div className="overflow-hidden transition-all ease-in-out">
                  {!expanded && (
                    <button className="text-blue-500 mt-2 focus:outline-none" onClick={toggleExpanded}>
                      Read More
                    </button>
                  )}
                  {expanded && (
                    <button className="text-blue-500 mt-2 focus:outline-none" onClick={toggleExpanded}>
                      Show Less
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="md:w-10/12 w-22/23 flex items-center justify-center flex-col">
          {cardsToRender.map((book) => (
            <>
              <BookSectionCard
                key={book.id}
                isOpen={expandedSections[book.number - 1]}
                name={book.name}
                sectionNumber={book.number}
                onClick={() => {
                  const newExpandedSections = [...expandedSections];
                  newExpandedSections[book.number - 1] = !newExpandedSections[book.number - 1];
                  setExpandedSections(newExpandedSections);
                }}
              />
              <Accordion className="w-full" initial={false} open={expandedSections[book.number - 1]}>
                <div className="grid w-full 3xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2">
                  {book.chapters?.map((chapter) => (
                    <ChapterCard
                      key={chapter.id}
                      bookId={book?.id}
                      bookSectionNumber={chapter.bookSection}
                      chapter={chapter.number}
                      chapterNumber={chapter.number}
                      name={chapter.name || ''}
                    />
                  ))}
                </div>
              </Accordion>
            </>
          ))}
        </div>
      </div>
      <Footer />
    </Layout>
  );
}
